import { useState, useEffect, Fragment } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSmall } from "lucide-react";
import { toast } from "react-toastify";
import { getAuditLog, AUDIT_ACTIONS, AUDIT_ENTITY_TYPES } from "./supportApi";

// "article.publish" -> "Article published". Built from the action name so a new backend
// action still reads sensibly instead of showing a raw dotted string.
const VERB_LABELS = {
  create: "created",
  update: "updated",
  delete: "deleted",
  publish: "published",
  submit_review: "submitted for review",
  make_internal: "made internal only",
  deprecate: "deprecated",
  revert_draft: "reverted to draft",
  restore: "restored",
  ai_enable: "AI turned on",
  ai_disable: "AI turned off",
  assign: "assigned",
  status_change: "status changed",
  register: "registered",
  claim: "claimed",
  unassign: "unassigned",
  resolve: "resolved",
};

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

const actionLabel = (action) => {
  const [entity, verb] = action.split(".");
  return `${titleCase(entity)} ${VERB_LABELS[verb] || titleCase(verb || "")}`.trim();
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US");
};

const EMPTY_FILTERS = {
  actorUsername: "",
  action: "",
  entityType: "",
  from: "",
  to: "",
};

/** Renders a before/after object, or a dash when the value was too long to store. */
function ValueList({ values, fields }) {
  if (!values || Object.keys(values).length === 0) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  return (
    <div className="space-y-0.5">
      {fields.map((f) =>
        f in values ? (
          <div key={f} className="font-mono text-[11px]">
            <span className="text-muted-foreground/70">{f}:</span>{" "}
            <span className="text-foreground">{String(values[f] ?? "null")}</span>
          </div>
        ) : null,
      )}
    </div>
  );
}

export default function ActivityLog() {
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const limit = 20;
  const totalPages = Math.max(1, Math.ceil(pagination.total / limit));

  useEffect(() => {
    const fetchLog = async () => {
      setIsLoading(true);
      try {
        const data = await getAuditLog(filters, page, limit);
        setEntries(data.entries || []);
        setPagination({
          total: data.total ?? 0,
          page: data.page ?? page,
          limit: data.limit ?? limit,
        });
      } catch (error) {
        console.error("Error fetching activity log:", error);
        toast.error("Failed to load the activity log");
      } finally {
        setIsLoading(false);
      }
    };
    fetchLog();
  }, [filters, page]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const selectClass =
    "rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Activity <span className="text-gradient-ginox">Log</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Who changed what in the help center. Reads are not recorded.
        </p>
      </div>

      <div className="space-y-5">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Actor
            </label>
            {/* The query parameter is still called actorUsername — it is a text snapshot in
                entries already written, so renaming it would change what those entries mean.
                What goes into it now is an admin panel id, or an agent's company email. */}
            <input
              type="text"
              value={filters.actorUsername}
              onChange={(e) => setFilter("actorUsername", e.target.value.trim())}
              placeholder="687938d5e59619adf26f7b1f"
              className={`${selectClass} min-w-[130px] placeholder:text-muted-foreground/50`}
            />
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Action
            </label>
            <select
              value={filters.action}
              onChange={(e) => setFilter("action", e.target.value)}
              className={`${selectClass} min-w-[190px]`}
            >
              <option value="">All actions</option>
              {AUDIT_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {actionLabel(a)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Entity
            </label>
            <select
              value={filters.entityType}
              onChange={(e) => setFilter("entityType", e.target.value)}
              className={`${selectClass} min-w-[150px]`}
            >
              <option value="">All entities</option>
              {AUDIT_ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {titleCase(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              From
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilter("from", e.target.value)}
              className={selectClass}
            />
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              To
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilter("to", e.target.value)}
              className={selectClass}
            />
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setPage(1);
              }}
              className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {hasFilters
              ? "No activity matches these filters."
              : "Nothing recorded yet."}
          </p>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {pagination.total} {pagination.total === 1 ? "entry" : "entries"}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
                <thead>
                  <tr>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      When
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Who
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Action
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Entity
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Changes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const isOpen = expandedId === entry.id;
                    const fields = entry.changedFields || [];
                    return (
                      <Fragment key={entry.id}>
                        <tr
                          onClick={() => setExpandedId(isOpen ? null : entry.id)}
                          className="hover:bg-white/[0.04] transition cursor-pointer"
                        >
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(entry.createdAt)}
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm">
                            {entry.actorType === "api_key" ? (
                              // The shared key identifies nobody, so there is no person to
                              // show — say so rather than leaving the cell blank.
                              <span className="text-muted-foreground/70 italic">
                                API key
                              </span>
                            ) : (
                              <>
                                <span className="text-foreground">
                                  {entry.actorName || "Unknown"}
                                </span>
                                {entry.actorUsername && (
                                  <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                                    {entry.actorUsername}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground whitespace-nowrap">
                            {actionLabel(entry.action)}
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground max-w-xs">
                            <span className="line-clamp-1">
                              {entry.entityLabel || entry.entityId || "—"}
                            </span>
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm">
                            <div className="flex items-center gap-1.5">
                              {isOpen ? (
                                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              ) : (
                                <ChevronRightSmall className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              )}
                              <span className="text-muted-foreground line-clamp-1">
                                {fields.length ? fields.join(", ") : "—"}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td
                              colSpan={5}
                              className="border-b border-border/40 bg-white/[0.02] px-4 py-4"
                            >
                              <div className="grid gap-6 md:grid-cols-2">
                                <div>
                                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                    Before
                                  </p>
                                  <ValueList values={entry.before} fields={fields} />
                                </div>
                                <div>
                                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                    After
                                  </p>
                                  <ValueList values={entry.after} fields={fields} />
                                </div>
                              </div>
                              {/* Long values (an article body) are recorded by field name
                                  only, so a listed field with no value is expected. */}
                              {fields.some(
                                (f) =>
                                  !(entry.before && f in entry.before) &&
                                  !(entry.after && f in entry.after),
                              ) && (
                                <p className="mt-3 text-[11px] text-muted-foreground/70">
                                  Some values are too long to record — only the field name
                                  is kept. Use the article's version history to see the
                                  text.
                                </p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between py-3 text-xs font-mono text-muted-foreground border-t border-border/40">
                <span className="tabular-nums">
                  {(page - 1) * limit + 1} -{" "}
                  {Math.min(page * limit, pagination.total)} of {pagination.total}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="tabular-nums">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
